import { CoreImageView } from '@core/widgets';
import { useApp, useBehavior, useSubscribeOnWsUpdate, useUpdateWidget, useUpdateWidgetValue } from '@libs/hooks';
import { ActionActionTypeEnum, AssetsSvgMap, TWidget, TWidgetProps } from '@libs/types';
import { ConditionalRender, currencyFormat, eventBus, significantDecimalPlaces } from '@libs/utils';
import clsx from 'clsx';
import React, { Activity, useEffect, useEffectEvent, useMemo } from 'react';
import { TBalanceData } from '../../types/data';
import { getInlineStyles } from '../../utils/styles';
import { getHref } from '../Image/imageUtils';
import { TextCommon } from '../UI/Text/TextCommon';
import SelectBalanceBottomSheet from './SelectBalanceBottomSheet';
import styles from './styles/balance.module.css';
import { getIconForCurrencyIso } from './utils/balanceUtils';

const selectedBalance = 'consumer3_balance_widget_selected_balance';

export const Balance: React.FC<TWidgetProps<TBalanceData>> = ({ widget }) => {
    const data = widget.data ?? {};
    const { updateWidgetValue } = useUpdateWidgetValue(widget);
    const storageKey = `${selectedBalance}_${widget.internalId}`;

    const restoreSavedBalance = useEffectEvent(() => {
        const savedId = localStorage.getItem(storageKey);
        if (savedId) {
            if (Array.isArray(data.balances) && data.balances?.length > 0) {
                const currentBalance = data.balances.find((b) => b?.id === savedId);
                if (currentBalance?.id) {
                    updateWidgetValue(currentBalance.id);
                }
            }
        }
    });

    useEffect(() => {
        restoreSavedBalance();
    }, []);

    const { setPopup } = useApp();
    const { updateWidget } = useUpdateWidget(widget.uid);
    const { isHidden } = useBehavior(widget);

    const currentBalance = useMemo(() => {
        if (Array.isArray(data.balances) && data.balances?.length > 0) {
            const currentBalance = data.balances.find((b) => b?.id === data.value) ?? data.balances[0];
            return {
                currentBalance: currentBalance,
                showArrow: data.balances.length > 1 || data.canGetPaysendCard || data.canExchange || data.canAddCurrency,
            };
        }
        return null;
    }, [data.balances, data.canAddCurrency, data.canExchange, data.canGetPaysendCard, data.value]);

    const arrActions = useMemo(() => widget.actions?.find((a) => a?.actionType === ActionActionTypeEnum.onTimer), [widget.actions]);

    useSubscribeOnWsUpdate(arrActions);

    useEffect(() => {
        if (widget.internalId) {
            eventBus.subscribeWS(widget.internalId, (w) => {
                if (w) {
                    updateWidget(w as unknown as TWidget<TBalanceData>);
                }
            });
        }
        return () => {
            eventBus.unsubscribeWsSpecificWidget(widget.internalId);
        };
    }, [updateWidget, widget.internalId]);

    const cb = currentBalance?.currentBalance;
    const hasCurrencyIcon = !!getIconForCurrencyIso(cb?.iso);

    if (cb) {
        const decimalLength = significantDecimalPlaces(cb.balance) > 0 ? 2 : 0;
        return (
            <Activity mode={isHidden ? 'hidden' : 'visible'}>
                <div data-test={widget.uid} className={styles.balanceContainer} style={getInlineStyles({ margin: widget.options?.margin })}>
                    <div
                        className={clsx(styles.sumContainer, currentBalance.showArrow && styles.showCursor)}
                        key={cb.id}
                        onClick={() => {
                            if (currentBalance.showArrow) {
                                setPopup(<SelectBalanceBottomSheet value={cb.id} widget={widget} storageKey={storageKey} />);
                            }
                        }}
                    >
                        <div data-test={widget.uid + '_amount'} className={styles.sum}>
                            <ConditionalRender condition={hasCurrencyIcon}>
                                <TextCommon
                                    isWidget={false}
                                    content={currencyFormat(cb.balance, cb.iso, decimalLength) as string}
                                    options={{
                                        maxLines: 1,
                                        enableAutoResize: true,
                                        variant: 'display1',
                                        textColor: 'black',
                                    }}
                                />
                            </ConditionalRender>
                            <ConditionalRender condition={!hasCurrencyIcon}>
                                <TextCommon
                                    isWidget={false}
                                    content={`${cb.balance} ${cb.iso?.toLowerCase()}`}
                                    options={{
                                        maxLines: 1,
                                        enableAutoResize: true,
                                        variant: 'display1',
                                        textColor: 'black',
                                    }}
                                    textTransformation={[
                                        {
                                            text: `${cb.balance}`,
                                            precision: decimalLength,
                                            useLocaleNumberFormat: true,
                                        },
                                        {
                                            text: cb.iso?.toLowerCase(),
                                            smallCaps: true,
                                        },
                                    ]}
                                />
                            </ConditionalRender>
                        </div>
                        <div data-test={widget.uid + '_currency'} className={styles.pseudoDropdown}>
                            <TextCommon isWidget={false} content={cb.name} options={{ variant: 'secondary', textColor: 'grey_72_lc' }} />
                            {currentBalance.showArrow && (
                                <CoreImageView
                                    src={getHref(AssetsSvgMap.ic_chevron_down_small)}
                                    styleProps={{
                                        width: 16,
                                        height: 16,
                                        iconColor: `var(--color-grey_72_lc)`,
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </Activity>
        );
    } else {
        return false;
    }
};
